/**
 * W5 control + portal: endpoints and shapes (API: apps/api/src/control, WMS-API.md §23–§30).
 * Kept apart from lib/inbound.ts and lib/outbound.ts (one file per sprint).
 */
import { parseDateRange } from '../components/ui/filter-bar';

export const CEP = {
  counts: '/counts',
  count: (id: string) => `/counts/${id}`,
  countScan: (id: string) => `/counts/${id}/scan`,
  countSubmit: (id: string) => `/counts/${id}/submit`,
  countApprove: (id: string) => `/counts/${id}/approve`,
  countCancel: (id: string) => `/counts/${id}/cancel`,
  countRelocate: (resultId: string) => `/counts/results/${resultId}/relocate`,
  aging: '/reports/aging',
  agingParcels: '/reports/aging/parcels',
  reminders: '/reports/reminders',
  kpi: '/reports/kpi',
  kpiCsv: (key: string) => `/reports/kpi/${key}.csv`,
  billing: '/reports/billing',
  events: '/audit/events',
  access: '/audit/access',
  anonymisation: '/audit/anonymisation',
  anonymiseNow: '/audit/anonymisation/run',
  dsar: '/audit/dsar',
  evidence: (parcelId: string) => `/parcels/${parcelId}/evidence.pdf`,
  webhooks: '/webhooks',
  webhook: (id: string) => `/webhooks/${id}`,
  webhookDeliveries: (id: string) => `/webhooks/${id}/deliveries`,
  webhookTest: (id: string) => `/webhooks/${id}/test`,
  portalSummary: '/portal/summary',
  portalRts: '/portal/rts',
  portalKeys: '/portal/api-keys',
  portalKey: (id: string) => `/portal/api-keys/${id}`,
  readerGateway: '/portal/reader-gateway',
  track: (tenant: string, code: string) => `/public/track/${encodeURIComponent(tenant)}/${encodeURIComponent(code)}`,
} as const;

export type CountStatus = 'PLANNED' | 'IN_PROGRESS' | 'SUBMITTED' | 'APPROVED' | 'CANCELLED';
export const COUNT_STATUSES: CountStatus[] = ['PLANNED', 'IN_PROGRESS', 'SUBMITTED', 'APPROVED', 'CANCELLED'];

export interface CountRow {
  id: string;
  taskNo: string;
  siteId: string;
  mode: 'RFID_SWEEP' | 'BARCODE';
  status: CountStatus;
  purpose: string;
  assignedToId: string | null;
  assignedTo: string | null;
  approvedBy: string | null;
  dueAt: string | null;
  expectedCount: number;
  foundCount: number;
  missingCount: number;
  unexpectedCount: number;
  locationCount: number;
  createdAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
}

export interface CountLocation { locationId: string; code: string | null; countedAt: string | null; accurate: boolean | null; expected: number; found: number; missing: number; unexpected: number; unknown: number }
export interface CountResultRow {
  id: string;
  locationId: string;
  locationCode: string | null;
  parcelId: string | null;
  parcelNo: string | null;
  parcelStatus: string | null;
  code: string | null;
  result: 'FOUND' | 'MISSING' | 'UNEXPECTED' | 'UNKNOWN';
  expectedLocationCode: string | null;
  action: 'NONE' | 'RELOCATED' | 'MISSING_EXCEPTION' | 'MARKED_FOUND' | 'ADJUSTED';
}
export interface CountDetail extends CountRow { locations: CountLocation[]; results: CountResultRow[] }

export interface AgingDashboard {
  buckets: string[];
  starts: number[];
  totals: number[];
  total: number;
  byClient: { clientId: string; code: string; name: string; counts: number[] }[];
  byZone: { zoneId: string | null; code: string; counts: number[] }[];
}

export interface KpiValue { value: number | null; n: number; [k: string]: number | null }
export interface KpiResult {
  from: string;
  to: string;
  kpis: Record<KpiKey, KpiValue>;
  targets: Partial<Record<KpiKey, { target: number; better: 'higher' | 'lower'; unit: string }>>;
}
export type KpiKey = 'dockToStock' | 'scanCompliance' | 'misSortRate' | 'locationAccuracy' | 'onTimeDispatch' | 'exceptionRate' | 'manifestClean' | 'rfidReadRate' | 'adoption' | 'perLabourHour';

/** PRD §14 order, with how each value reads. */
export const KPI_META: { key: KpiKey; label: string; unit: 'h' | '%' | 'n'; hint: string }[] = [
  { key: 'dockToStock', label: 'Dock-to-stock', unit: 'h', hint: 'Arrival scan to parcel in its bin (average; P90 below).' },
  { key: 'scanCompliance', label: 'Scan compliance', unit: '%', hint: 'Parcels handled with no typed (manual) custody step.' },
  { key: 'misSortRate', label: 'Mis-sort rate', unit: '%', hint: 'Blocked mis-sorts ÷ sort attempts.' },
  { key: 'locationAccuracy', label: 'Location accuracy', unit: '%', hint: 'Counted bins with zero variance ÷ counted bins.' },
  { key: 'onTimeDispatch', label: 'On-time dispatch', unit: '%', hint: 'Parcels handed over by the route cut-off ÷ handed over.' },
  { key: 'exceptionRate', label: 'Exception resolution', unit: 'h', hint: 'Median hours from open to resolved.' },
  { key: 'manifestClean', label: 'Manifests without override', unit: '%', hint: 'Closed manifests that reconciled cleanly.' },
  { key: 'rfidReadRate', label: 'RFID read rate', unit: '%', hint: 'Tags read ÷ tags expected (RFID counts and reader passes).' },
  { key: 'adoption', label: 'RFID adoption', unit: '%', hint: 'Parcels received with an RFID tag.' },
  { key: 'perLabourHour', label: 'Scans per labour hour', unit: 'n', hint: 'Custody steps by people ÷ active user-hours.' },
];

export interface EventLogRow { id: string; type: string; parcelId: string | null; parcelNo: string | null; statusFrom: string | null; statusTo: string | null; from: string | null; to: string | null; user: string | null; role: string | null; device: string | null; method: string; occurredAt: string; recordedAt: string; reasonCode: string | null; note: string | null; hash: string | null }
export interface AccessRow { id: string; at: string; action: string; user: string | null; clientCode: string | null; parcelId: string | null; parcelNo: string | null; subject: string | null; rowCount: number | null; fields: string[]; purpose: string | null; ip: string | null }
export interface AnonRun { id: string; startedAt: string; finishedAt: string | null; parcelsAnonymised: number; linesAnonymised: number; filesPurged: number; skippedHeld: number; report: { clientCode: string; retentionDays: number; parcels: number; skippedHeld: number }[] | null }

export const WEBHOOK_EVENTS = ['RECEIVED', 'STORED', 'EXCEPTION', 'HOLD', 'DISPATCHED', 'DELIVERED', 'RTS'] as const;
export interface WebhookRow { id: string; clientId: string | null; url: string; events: string[]; active: boolean; failures: number; lastStatus: number | null; lastDeliveryAt: string | null; createdAt: string }
export interface DeliveryRow { id: string; eventKey: string; eventType: string; status: 'PENDING' | 'DELIVERED' | 'FAILED' | 'GAVE_UP'; attempts: number; responseCode: number | null; responseBody: string | null; createdAt: string; deliveredAt: string | null }
export interface PortalKey { id: string; name: string; prefix: string; scopes: string[]; createdAt: string; lastUsedAt: string | null; expiresAt: string | null; revokedAt: string | null; active: boolean }

/** Manila "today" and N days back, as YYYY-MM-DD. */
export function manilaDate(offsetDays = 0): string {
  return new Date(Date.now() + 8 * 3_600_000 - offsetDays * 86_400_000).toISOString().slice(0, 10);
}

/** FilterBar date value (preset or "from..to") → explicit from/to (default: last 30 days). */
export function rangeOf(v: string | undefined): { from: string; to: string } {
  const r = parseDateRange(v);
  if (r) return { from: r.from || manilaDate(365), to: r.to || manilaDate(0) };
  if (v === '7d') return { from: manilaDate(6), to: manilaDate(0) };
  if (v === '12m') return { from: manilaDate(364), to: manilaDate(0) };
  if (v === 'year') return { from: `${manilaDate(0).slice(0, 4)}-01-01`, to: manilaDate(0) };
  return { from: manilaDate(29), to: manilaDate(0) };
}

/** A KPI value as text. */
export function fmtKpi(v: number | null | undefined, unit: 'h' | '%' | 'n'): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  if (unit === '%') return `${(v * 100).toFixed(v < 0.01 && v > 0 ? 2 : 1)}%`;
  if (unit === 'h') return `${v.toFixed(1)} h`;
  return v.toFixed(1);
}

/** Pure: on target? (null when there is no value or no target). */
export function onTarget(v: number | null | undefined, t?: { target: number; better: 'higher' | 'lower' }): boolean | null {
  if (v === null || v === undefined || !t) return null;
  return t.better === 'higher' ? v >= t.target : v <= t.target;
}
